import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';

// Secondi per un passaggio completo di tutte le 20 testimonianze (più alto = più lento)
const PASS_DURATION_SECONDS = 90;
// Dopo un tocco, ripresa dello scorrimento
const TOUCH_RESUME_MS = 1500;
// Accelerazione/frenata morbida quando si ferma o riparte (0-1, più basso = più morbido)
const SPEED_EASING = 0.06;

const TESTIMONIALS = [
  { author: 'giuly_82', text: 'Finalmente vado a lavoro tranquilla. So sempre se mia madre ha preso le medicine grazie al diario digitale.' },
  { author: 'martii97', text: "L'interfaccia è facilissima, anche la nostra badante che non è tecnologica la usa senza problemi." },
  { author: 'sere.nella79', text: "Il Tasto Scudo per le emergenze vale da solo l'abbonamento. Una sicurezza incredibile." },
  { author: 'vale.rossi88', text: 'Leggere il report la domenica sera mi dà un senso di pace. Tutto tracciato e trasparente.' },
  { author: 'chiaretta_93', text: "Mio nonno non ha dovuto scaricare nessuna app. Geniale l'idea della Stazione Digitale in casa." },
  { author: 'fra.ncesca_80', text: 'Sapere in tempo reale se hanno pranzato mi toglie un grosso peso dal cuore ogni giorno.' },
  { author: 'ale_galli91', text: 'La mappa GPS per le passeggiate è precisissima. Ora so sempre dove si trova papà col suo assistente.' },
  { author: 'silvi_etta85', text: 'Non serve installare niente e funziona subito. I miei genitori sono molto più sicuri e noi figli più sereni.' },
  { author: 'elee.89', text: "L'operatore ci mette 2 secondi a confermare le terapie. Niente più chiamate a vuoto per controllare." },
  { author: 'marty.92_', text: 'CuraCheck fa esattamente quello che promette: zero fronzoli, tanta affidabilità.' },
  { author: 'matteo_80', text: "Da quando lo usiamo, io e mio fratello abbiamo tutto sotto controllo sull'app senza doverci alternare per chiamare." },
  { author: 'andrea.lux75', text: 'Ottimo per chi lavora lontano da casa e non può passare tutti i giorni. Ti fa sentire vicino alla famiglia.' },
  { author: 'lory_88', text: 'La nostra assistente si trova benissimo, è super intuitivo e non ci sono barriere linguistiche.' },
  { author: 'davide_m85', text: 'Il fatto che non sembri un sistema di sorveglianza invadente ha convinto mio padre ad accettarlo volentieri.' },
  { author: 'ste_fano87', text: "I report settimanali sono perfetti. Li stampo e li porto al medico di base per fargli vedere l'andamento." },
  { author: 'giovi_81', text: 'Niente più chat WhatsApp disordinate! Ora le notifiche sono precise, ufficiali e immediate. Una svolta.' },
  { author: 'marco.t79', text: 'In 5 minuti avevamo già impostato tutta la routine della giornata. Piattaforma fantastica.' },
  { author: 'ale_b84', text: 'Il piano annuale ha un prezzo onestissimo per la tranquillità che ti restituisce. Consigliatissimo.' },
  { author: 'fede_rico92', text: 'Pulito, veloce, notifiche tempestive. Finalmente uno strumento serio per gestire l\'assistenza domiciliare.' },
  { author: 'luca_89x', text: "Mi ha tolto quell'ansia perenne di non sapere cosa succede a casa. Soldi spesi benissimo per la nostra serenità." },
];

const TestimonialCard = ({ testimonial }) => (
  <div className="h-full bg-white rounded-2xl p-5 shadow-md hover:shadow-lg transition-all border border-[#4A7C59]/10 flex flex-col">
    <div className="flex gap-1 mb-3">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
      ))}
    </div>
    <p className="text-[#2C3E50] text-sm mb-4 leading-relaxed flex-1">"{testimonial.text}"</p>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gradient-to-br from-[#4A7C59] to-[#5DADE2] rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-md">
        {testimonial.author.charAt(0).toUpperCase()}
      </div>
      <p className="font-bold text-[#2C3E50] text-sm">{testimonial.author}</p>
    </div>
  </div>
);

// Una serie di 20 card; la seconda copia (aria-hidden) rende il giro continuo senza salti
const CardSet = ({ copy }) => (
  <div className="flex shrink-0 items-stretch" aria-hidden={copy ? 'true' : undefined}>
    {TESTIMONIALS.map((testimonial, index) => (
      <div
        key={testimonial.author}
        className="w-[280px] sm:w-[340px] shrink-0 pr-4 py-2"
        data-testid={copy ? undefined : `testimonial-slide-${index + 1}`}
      >
        <TestimonialCard testimonial={testimonial} />
      </div>
    ))}
  </div>
);

const usePrefersReducedMotion = () => {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mql = window.matchMedia?.(query);
    if (!mql) return undefined;
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

// Scorrimento a velocità costante; si ferma con il mouse sopra o durante il tocco
const useMarquee = (trackRef, enabled) => {
  const paused = useRef(false);
  const resumeTimer = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let frame;
    let last = performance.now();
    let offset = 0;
    let speedFactor = 1;
    const step = (now) => {
      const dt = Math.min(now - last, 50);
      last = now;
      const track = trackRef.current;
      const setWidth = track ? track.scrollWidth / 2 : 0;
      speedFactor += ((paused.current ? 0 : 1) - speedFactor) * SPEED_EASING;
      if (setWidth > 0) {
        offset = (offset + (setWidth / (PASS_DURATION_SECONDS * 1000)) * dt * speedFactor) % setWidth;
        track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(resumeTimer.current);
    };
  }, [trackRef, enabled]);

  const setPaused = (value) => {
    clearTimeout(resumeTimer.current);
    paused.current = value;
  };
  const resumeLater = () => {
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => { paused.current = false; }, TOUCH_RESUME_MS);
  };

  return {
    onPointerEnter: (e) => { if (e.pointerType === 'mouse') setPaused(true); },
    onPointerLeave: (e) => { if (e.pointerType === 'mouse') setPaused(false); },
    onPointerDown: (e) => { if (e.pointerType !== 'mouse') setPaused(true); },
    onPointerUp: (e) => { if (e.pointerType !== 'mouse') resumeLater(); },
    onPointerCancel: (e) => { if (e.pointerType !== 'mouse') resumeLater(); },
  };
};

export const TestimonialsCarousel = () => {
  const trackRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();
  const handlers = useMarquee(trackRef, !reducedMotion);

  if (reducedMotion) {
    return (
      <div className="overflow-x-auto px-4 sm:px-6" data-testid="testimonials-carousel" data-motion="off">
        <CardSet />
      </div>
    );
  }

  return (
    <div className="overflow-hidden" data-testid="testimonials-carousel" data-motion="on" {...handlers}>
      <div ref={trackRef} className="flex w-max will-change-transform" data-testid="testimonials-track">
        <CardSet />
        <CardSet copy />
      </div>
    </div>
  );
};
